import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import Layout from "../components/Layout";
import { labelCategorie } from "./VehiculesListe";
import { labelTypeOperation, libelleOperation } from "./OperationForm";
import { formaterEuros, formaterKm, formaterDate, formaterDecimal, periodeInversee, MESSAGE_PERIODE_INVERSEE } from "../format";
import { SqueletteCartes } from "../components/Squelette";
import {
  labelTypeRecurrence,
  labelStatutRecurrence,
  texteJoursRestants,
  classeUrgence,
} from "../recurrences";
import { useModules } from "../modules";
import DocumentsVehicule from "../components/DocumentsVehicule";
import ReferencesVehicule from "../components/ReferencesVehicule";
import FinancementVehicule from "../components/FinancementVehicule";
import LocationVehicule from "../components/LocationVehicule";
import { useVehiculesLoues } from "../locationsEnCours";
import ModaleConfirmation from "../components/ModaleConfirmation";

function TableInspections({ inspections, onOuvrir }) {
  return (
    <table className="tableau">
      <thead>
        <tr>
          <th>Date</th>
          <th>N° contrôle</th>
          <th>Statut</th>
        </tr>
      </thead>
      <tbody>
        {inspections.map((insp) => (
          <tr key={insp.id} className="ligne-cliquable" onClick={() => onOuvrir(insp)}>
            <td>{formaterDate(insp.date_controle)}</td>
            <td>{insp.numero_controle || "—"}</td>
            <td>
              <span className={insp.statut === "terminee" ? "badge-cat" : "badge"}>
                {insp.statut === "terminee" ? "Terminée" : "En cours"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Champ({ label, valeur }) {
  return (
    <div className="champ">
      <span className="champ-label">{label}</span>
      <span className="champ-valeur">
        {valeur === null || valeur === undefined || valeur === "" ? "—" : String(valeur)}
      </span>
    </div>
  );
}

// Date minimale pour un report : aujourd'hui.
const MIN_DATE_REPORT_FICHE = new Date().toISOString().slice(0, 10);
// Date MAXIMALE pour un contrôle déjà effectué : aujourd'hui aussi. Même
// valeur, deux usages opposés — un report regarde devant, un contrôle
// derrière. Deux constantes plutôt qu'une : le jour où l'une bouge, l'autre
// ne doit pas suivre par accident.
const AUJOURDHUI_FICHE = new Date().toISOString().slice(0, 10);
const dateDemainFiche = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function VehiculeFiche({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicule, setVehicule] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");

  const [couts, setCouts] = useState(null);
  const [operations, setOperations] = useState([]);
  const [recurrences, setRecurrences] = useState([]);
  const modules = useModules();
  const loues = useVehiculesLoues(user, modules);
  // LE MECANICIEN VOIT LES COUTS DE CE CAMION, PAS LE FINANCEMENT.
  //
  // Arbitrage de Yanis (25/09) : il a ce vehicule sous les yeux, et savoir
  // qu'il coute cher en entretien fait partie de son metier. Ce qui lui reste
  // ferme, c'est la situation financiere de l'entreprise -- la synthese de
  // flotte du tableau de bord, et le financement.
  //
  // Ce drapeau evite donc UN appel voue au 403 : sans lui, le bloc
  // Financement se monterait chez un client qui a souscrit le module, et
  // afficherait une erreur alors que le serveur a raison.
  const estMecanicien = user.role === "mecanicien";
  // Catalogue servi par le serveur : c'est lui qui dit quels types existent et
  // avec quelle periodicite. Le recopier ici finirait par diverger, et l'ecran
  // proposerait un type que le serveur refuse.
  const [typesEcheance, setTypesEcheance] = useState([]);
  const [modalEcheance, setModalEcheance] = useState(false);
  // Erreur PROPRE a la modale. Passer par le bandeau general de la page ne
  // marche pas : il s'affiche derriere le voile, hors de l'ecran des que la
  // page est deroulee -- l'utilisateur voit alors un bouton sans effet.
  const [erreurEcheance, setErreurEcheance] = useState("");
  const [nouvelleEcheance, setNouvelleEcheance] = useState({
    type_recurrence: "assurance",
    date_prochaine_echeance: "",
  });
  const [inspections, setInspections] = useState([]);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  // État de la modale de report
  const [modalReport, setModalReport] = useState(null); // recurrence reportée
  const [reportDate, setReportDate] = useState("");
  const [reportMotif, setReportMotif] = useState("");
  const [reportEnvoi, setReportEnvoi] = useState(false);
  // Confirmation en cours : les proprietes de la modale, ou null.
  // UN seul etat pour toutes les actions a confirmer de cette page -- deux
  // confirmations ne peuvent pas etre ouvertes en meme temps, et une seule
  // definition evite qu'une des cinq oublie de desactiver son bouton.
  const [confirmation, setConfirmation] = useState(null);
  const [motifExclusion, setMotifExclusion] = useState("Vendu");
  // Date reelle du controle, saisie dans la modale « effectuée ».
  const [dateEffectuee, setDateEffectuee] = useState("");

  useEffect(() => {
    api
      .get(`/vehicules/${id}`)
      .then(({ data }) => setVehicule(data))
      .catch((err) =>
        setErreur(err?.response?.status === 404 ? "Véhicule introuvable." : "Erreur de chargement.")
      )
      .finally(() => setChargement(false));
  }, [id]);

  const chargerCouts = useCallback(() => {
    if (periodeInversee(dateDebut, dateFin)) return;
    const params = {};
    if (dateDebut) params.date_debut = dateDebut;
    if (dateFin) params.date_fin = dateFin;
    Promise.all([
      api.get(`/vehicules/${id}/couts`, { params }),
      api.get(`/vehicules/${id}/operations`, { params }),
    ])
      .then(([rCouts, rOps]) => {
        setCouts(rCouts.data);
        setOperations(rOps.data);
      })
      .catch(() => setErreur("Impossible de charger les coûts."));
  }, [id, dateDebut, dateFin]);

  useEffect(() => {
    chargerCouts();
  }, [chargerCouts]);

  const chargerRecurrences = useCallback(() => {
    api
      .get(`/vehicules/${id}/recurrences`)
      .then(({ data }) => setRecurrences(data))
      .catch(() => setErreur("Impossible de charger les échéances."));
  }, [id]);

  useEffect(() => {
    chargerRecurrences();
  }, [chargerRecurrences]);

  // Charge une fois : le catalogue ne change pas pendant une session.
  useEffect(() => {
    api
      .get("/echeances/types")
      .then(({ data }) => setTypesEcheance(data))
      .catch(() => {
        /* Sans catalogue on ne peut pas AJOUTER d'echeance, mais l'ecran
           reste utilisable pour celles qui existent -- les libelles ont un
           repli local. */
      });
  }, []);

  /** Libelle d'un type : le serveur d'abord, le repli local ensuite. */
  const libelleType = (valeur) =>
    typesEcheance.find((t) => t.valeur === valeur)?.libelle
    || labelTypeRecurrence(valeur);

  const ajouterEcheance = () => {
    api
      .post(`/vehicules/${id}/recurrences`, nouvelleEcheance)
      .then(() => {
        setModalEcheance(false);
        setErreurEcheance("");
        setNouvelleEcheance({ type_recurrence: "assurance", date_prochaine_echeance: "" });
        chargerRecurrences();
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setErreurEcheance(typeof detail === "string" ? detail : "Ajout impossible.");
      });
  };

  const nePlusSuivre = (rec) => {
    setConfirmation({
      titre: "Ne plus suivre cette échéance ?",
      sousTitre: `${libelleType(rec.type_recurrence)} — ${vehicule?.immatriculation ?? ""}`,
      message: "L'historique est conservé : rien n'est supprimé.",
      libelleConfirmer: "Ne plus suivre",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/recurrences/${rec.id}`);
        chargerRecurrences();
      },
    });
  };

  useEffect(() => {
    api
      .get(`/vehicules/${id}/inspections`)
      .then(({ data }) => setInspections(data))
      .catch(() => setErreur("Impossible de charger les inspections."));
  }, [id]);

  const demarrerVgp = async () => {
    try {
      const { data } = await api.post(`/vehicules/${id}/inspections`, {
        type_inspection: "vgp_hayon",
      });
      navigate(`/vehicules/${id}/vgp/${data.id}`);
    } catch (err) {
      setErreur(
        err?.response?.status === 400
          ? "La VGP est réservée aux véhicules équipés d'un hayon."
          : "Impossible de démarrer la VGP."
      );
    }
  };

  const demarrerCt = async () => {
    try {
      const { data } = await api.post(`/vehicules/${id}/inspections`, {
        type_inspection: "controle_technique",
      });
      navigate(`/vehicules/${id}/ct/${data.id}`);
    } catch {
      setErreur("Impossible de démarrer le contrôle technique.");
    }
  };

  const inspectionsVgp = inspections.filter((i) => i.type_inspection === "vgp_hayon");
  const inspectionsCt = inspections.filter((i) => i.type_inspection === "controle_technique");

  const reinitialiserFiltre = () => {
    setDateDebut("");
    setDateFin("");
  };

  // ON DEMANDE LA DATE REELLE DU CONTROLE, et c'est toute la correction.
  //
  // Sans elle, la prochaine echeance partait de la PRECEDENTE : sur un camion
  // repris dont le dernier controle datait de 2016, chaque clic avancait d'un
  // an -- dix clics pour revenir a aujourd'hui, et dix controles supposes en
  // chemin. Une saisie remplace la boucle.
  //
  // La date du jour est proposee : c'est le cas courant, on saisit le controle
  // le jour meme. Elle se corrige d'un geste pour le cas de reprise.
  const marquerEffectuee = (rec) => {
    setDateEffectuee(AUJOURDHUI_FICHE);
    const aRenseigner = rec.date_a_renseigner;
    setConfirmation({
      titre: aRenseigner ? "Quand ce contrôle a-t-il eu lieu ?" : "Marquer comme effectuée ?",
      sousTitre: `${labelTypeRecurrence(rec.type_recurrence)} — ${vehicule?.immatriculation ?? ""}`,
      message: aRenseigner
        ? "La date affichée jusqu'ici était déduite de la mise en circulation, pas connue. "
          + "En saisissant celle du dernier contrôle réel, la prochaine échéance devient juste."
        : "L'échéance actuelle est conservée en historique, et la prochaine est calculée "
          + "à partir de la date saisie.",
      champDate: true,
      libelleConfirmer: aRenseigner ? "Enregistrer la date" : "Marquer effectuée",
      onConfirmer: async (dateSaisie) => {
        await api.post(`/recurrences/${rec.id}/effectuer`,
                       dateSaisie ? { date_effectuee: dateSaisie } : {});
        chargerRecurrences();
      },
    });
  };

  const ouvrirModalReport = (rec) => {
    setModalReport(rec);
    setReportDate(dateDemainFiche());
    setReportMotif("");
  };

  const soumettreReport = async () => {
    if (!reportDate) return;
    setReportEnvoi(true);
    try {
      await api.post(`/recurrences/${modalReport.id}/reporter`, {
        nouvelle_date: reportDate,
        motif: reportMotif.trim() || null,
      });
      chargerRecurrences();
      setModalReport(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErreur(typeof detail === "string" ? detail : "Impossible de reporter l'échéance.");
    } finally {
      setReportEnvoi(false);
    }
  };

  const supprimerOperation = (op) => {
    setConfirmation({
      titre: "Supprimer cette opération ?",
      sousTitre: `${libelleOperation(op)} du ${formaterDate(op.date_operation)}`,
      message:
        "Les pièces consommées, s'il y en a, sont rendues au stock. Cette action est irréversible.",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/operations/${op.id}`);
        chargerCouts();
      },
    });
  };

  // `window.prompt` bloquait l'onglet comme `window.confirm`, et n'affichait
  // qu'une ligne de texte nue : ni le camion concerne, ni ce que la sortie du
  // parc entraine. Le motif se saisit maintenant dans la modale.
  const exclure = () => {
    setMotifExclusion("Vendu");
    setConfirmation({
      titre: "Sortir ce véhicule du parc ?",
      sousTitre: vehicule?.immatriculation,
      message:
        "Il ne sera plus compté dans le parc ni dans les travaux à venir, et ses "
        + "échéances cessent d'être suivies. Son historique est conservé, et il "
        + "reste consultable dans « Sortis du parc ».",
      champMotif: true,
      libelleConfirmer: "Sortir du parc",
      danger: true,
      onConfirmer: async (motif) => {
        const { data } = await api.put(`/vehicules/${id}`, {
          exclu: true,
          date_exclusion: new Date().toISOString().slice(0, 10),
          motif_exclusion: (motif || "").trim() || null,
        });
        setVehicule(data);
        chargerRecurrences();
      },
    });
  };

  const reactiver = () => {
    setConfirmation({
      titre: "Réintégrer ce véhicule au parc ?",
      sousTitre: vehicule?.immatriculation,
      message:
        "Attention : les échéances réglementaires (contrôle technique, VGP) ne "
        + "seront pas recréées automatiquement. Ajoutez-les à la main si nécessaire.",
      libelleConfirmer: "Réintégrer",
      onConfirmer: async () => {
        const { data } = await api.put(`/vehicules/${id}`, {
          exclu: false,
          date_exclusion: null,
          motif_exclusion: null,
        });
        setVehicule(data);
        chargerRecurrences();
      },
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <button className="btn" onClick={() => navigate("/vehicules")}>
          ← Retour à la liste
        </button>
        {vehicule && (
          <div className="actions-groupe">
            {user.role === "admin" && (
              vehicule.exclu ? (
                <button className="btn-accent" onClick={reactiver}>
                  Réactiver le véhicule
                </button>
              ) : (
                <button className="btn" onClick={exclure}>
                  Exclure du parc
                </button>
              )
            )}
            <button className="btn-accent" onClick={() => navigate(`/vehicules/${id}/modifier`)}>
              Modifier
            </button>
          </div>
        )}
      </div>

      {chargement && <SqueletteCartes nombre={3} />}
      {erreur && <div className="erreur">{erreur}</div>}

      {vehicule && (
        <>
          {vehicule.exclu && (
            <div className="bandeau-exclusion">
              <strong>Véhicule exclu du parc</strong>
              {vehicule.motif_exclusion ? ` — ${vehicule.motif_exclusion}` : ""}
              {vehicule.date_exclusion
                ? ` · depuis le ${formaterDate(vehicule.date_exclusion)}`
                : ""}
            </div>
          )}

          <div className="entete-fiche">
            <h2>{vehicule.immatriculation}</h2>
            <span className="badge-cat">{labelCategorie(vehicule.categorie)}</span>
            {vehicule.hayon && <span className="badge">Hayon</span>}
            {vehicule.exclu && <span className="badge-exclu">Exclu</span>}
            {loues.has(vehicule.id) && <span className="badge">En location</span>}
          </div>

          <section className="carte">
            <h3>Informations véhicule</h3>
            <div className="grille-champs">
              <Champ label="Immatriculation" valeur={vehicule.immatriculation} />
              <Champ label="Catégorie" valeur={labelCategorie(vehicule.categorie)} />
              <Champ label="Marque" valeur={vehicule.marque} />
              <Champ label="Type / modèle" valeur={vehicule.type_modele} />
              <Champ label="N° châssis" valeur={vehicule.n_chassis} />
              <Champ label="N° série" valeur={vehicule.n_serie} />
              <Champ label="1re circulation" valeur={formaterDate(vehicule.date_premiere_circulation)} />
              <Champ label="Mise en service" valeur={formaterDate(vehicule.date_mise_en_service)} />
              <Champ label="Capacité (kg)" valeur={vehicule.capacite_kg} />
              <Champ label="Centre de gravité (mm)" valeur={vehicule.centre_gravite_mm} />
              <Champ label="N° parc" valeur={vehicule.n_parc} />
              <Champ label="Km initial" valeur={vehicule.km_initial} />
            </div>
          </section>

          <section className="carte">
            <h3>Hayon élévateur</h3>
            {vehicule.equipement_hayon ? (
              <div className="grille-champs">
                <Champ label="Marque" valeur={vehicule.equipement_hayon.marque} />
                <Champ label="Type" valeur={vehicule.equipement_hayon.type} />
                <Champ label="N° série" valeur={vehicule.equipement_hayon.n_serie} />
                <Champ label="Année" valeur={vehicule.equipement_hayon.annee} />
                <Champ label="Capacité de levage (kg)" valeur={vehicule.equipement_hayon.capacite_levage_kg} />
                <Champ label="Centre de gravité (mm)" valeur={vehicule.equipement_hayon.centre_gravite_mm} />
                <Champ label="Pression limiteur (bar)" valeur={vehicule.equipement_hayon.pression_limiteur_bar} />
                <Champ
                  label="Arrêt de rolls"
                  valeur={
                    vehicule.equipement_hayon.arret_de_rolls === null
                      ? "—"
                      : vehicule.equipement_hayon.arret_de_rolls
                      ? "Oui"
                      : "Non"
                  }
                />
              </div>
            ) : (
              <p className="vide">Ce véhicule n'est pas équipé d'un hayon.</p>
            )}
          </section>

          <section className="carte">
            <div className="entete-section">
              <h3>Échéances réglementaires</h3>
              {/* Reserve a l'administrateur, comme cote serveur : une echeance
                  engage la conformite de l'entreprise. */}
              {user.role === "admin" && typesEcheance.length > 0 && (
                <button
                  className="btn"
                  onClick={() => {
                    setErreurEcheance("");
                    setModalEcheance(true);
                  }}
                >
                  + Ajouter une échéance
                </button>
              )}
            </div>
            <p className="note-info">
              Marquer une échéance comme effectuée ne la supprime pas : elle est conservée en
              historique et la prochaine échéance est générée automatiquement.
            </p>

            {(() => {
              const actives = recurrences.filter((r) => r.active);
              const historique = recurrences.filter((r) => !r.active);
              return (
                <>
                  {actives.length === 0 ? (
                    <p className="vide">Aucune échéance active.</p>
                  ) : (
                    <div className="echeances-liste">
                      {actives.map((r) => (
                        <div key={r.id} className={"echeance-carte " + classeUrgence(r)}>
                          <div className="echeance-infos">
                            <span className="echeance-type">{libelleType(r.type_recurrence)}</span>
                            {/* LA DATE DÉDUITE NE S'AFFICHE PAS.
                                Elle existe en base — la colonne ne peut pas
                                être vide et le tri doit rester sensé — mais
                                l'écrire reviendrait à annoncer une échéance
                                de 2015 sur un camion enregistré ce matin.
                                On dit ce qu'on sait : la périodicité. La date,
                                on la demande. */}
                            <span className="echeance-date">
                              {r.date_a_renseigner
                                ? "Date du dernier contrôle inconnue"
                                : `Prochaine échéance : ${formaterDate(r.date_prochaine_echeance)}`}
                              {" · tous les "}
                              {r.intervalle_jours
                                ? `${r.intervalle_jours} jours`
                                : `${r.intervalle_mois} mois`}
                            </span>
                            <span className="jours">{texteJoursRestants(r.jours_restants, r)}</span>
                            {r.date_dernier_report && (
                              <span className="sous-info">
                                ↪ Reportée le {formaterDate(r.date_dernier_report)}
                                {r.motif_dernier_report ? ` — ${r.motif_dernier_report}` : ""}
                              </span>
                            )}
                          </div>
                          <div className="actions-groupe">
                            {user.role === "admin" && (
                              <button className="btn" onClick={() => ouvrirModalReport(r)}>
                                Reporter
                              </button>
                            )}
                            {/* Le libellé suit l'état : sur une date déduite,
                                on ne « marque pas effectué » un contrôle qu'on
                                n'a pas vu — on renseigne ce qui manque. */}
                            <button className="btn-accent" onClick={() => marquerEffectuee(r)}>
                              {r.date_a_renseigner
                                ? "Renseigner le dernier contrôle"
                                : "Marquer comme effectuée"}
                            </button>
                            {user.role === "admin" && (
                              <button className="lien-danger" onClick={() => nePlusSuivre(r)}>
                                Ne plus suivre
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {historique.length > 0 && (
                    <div className="echeances-historique">
                      <h4>Historique</h4>
                      <table className="tableau">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Échéance</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historique.map((r) => (
                            <tr key={r.id}>
                              <td>{libelleType(r.type_recurrence)}</td>
                              <td>{formaterDate(r.date_prochaine_echeance)}</td>
                              {/* Une ligne d'historique qui n'a pas ete effectuee a
                                  ete arretee. Y lire « A venir » ferait croire a un
                                  suivi toujours actif. */}
                              <td>
                                {r.statut === "effectue"
                                  ? labelStatutRecurrence(r.statut)
                                  : "Suivi arrêté"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </section>

          {/* Les papiers du camion. Module Parc : une carte grise n'est pas
              une option, c'est la piece qui identifie le vehicule. */}
          <DocumentsVehicule vehiculeId={vehicule.id} />

          {/* Ce que ce camion consomme : plaquettes, pneus, huile, filtres.
              Module Parc lui aussi -- la reference a commander vaut meme sans
              le stock. Avec le module Stock, chaque ligne dit en plus ce qu'il
              reste en magasin. */}
          {/* L'ECRAN SUIT LE SERVEUR (29/09/2026) : les routes des references
              sont ouvertes a tous les roles d'un client depuis toujours, mais
              l'ecran reservait l'ajout aux administrateurs. Le mecanicien -- qui
              change les plaquettes et sait lesquelles -- ne pouvait pas noter la
              reference. Demande de Yanis, travailleur compris. L'editeur en
              consultation reste sans bouton : il regarde, il ne saisit pas. */}
          <ReferencesVehicule
            vehiculeId={vehicule.id}
            peutModifier={["admin", "travailleur", "mecanicien"].includes(user.role)}
          />

          {/* Module optionnel : beaucoup de transporteurs achetent comptant et
              n'ont rien a suivre ici. Le serveur refuse de toute facon (403) ;
              on evite simplement de proposer la porte.

              VISIBLE PAR TOUS DEPUIS LE 18/08 : la consultation du
              financement a ete rouverte aux travailleurs, sur decision de
              Yanis. Elle avait ete fermee le 11/08 a la demande du patron
              d'Alpha Course -- un mecanicien y lisait le loyer et l'organisme
              preteur du camion. La reserve a ete posee, la decision assumee.

              `estAdmin` reste la barriere qui compte : elle commande les
              boutons d'ecriture. Un travailleur LIT, il ne saisit ni ne
              modifie un contrat -- et le serveur le refuse de toute facon.
              Le super-admin en consultation est dans le meme cas. */}
          {/* LOCATION (module neuf, coupé par défaut) : ce camion est-il loué,
              et à qui ? Le mécanicien n'y a pas accès (liste blanche serveur) ;
              le travailleur lit, l'administrateur ouvre une location. */}
          {!estMecanicien && modules.module_location && (
            <LocationVehicule vehicule={vehicule} estAdmin={user.role === "admin"} />
          )}

          {!estMecanicien && modules.module_financement && (
            <FinancementVehicule
              vehiculeId={vehicule.id}
              estAdmin={user.role === "admin"}
            />
          )}

          {/* Masque si le module VGP n'est pas souscrit. Le serveur refuse
              de toute facon (403) : on evite juste de proposer la porte. */}
          {modules.module_vgp && (
          <>
          <section className="carte">
            <div className="entete-section">
              <h3>Inspections VGP hayon</h3>
              {vehicule.hayon && (
                <button className="btn-accent" onClick={demarrerVgp}>
                  Démarrer une VGP
                </button>
              )}
            </div>
            {!vehicule.hayon ? (
              <p className="vide">Réservé aux véhicules équipés d'un hayon.</p>
            ) : inspectionsVgp.length === 0 ? (
              <p className="vide">Aucune VGP pour ce véhicule.</p>
            ) : (
              <TableInspections
                inspections={inspectionsVgp}
                onOuvrir={(insp) => navigate(`/vehicules/${id}/vgp/${insp.id}`)}
              />
            )}
          </section>

          <section className="carte">
            <div className="entete-section">
              <h3>Contrôle technique</h3>
              <button className="btn-accent" onClick={demarrerCt}>
                Démarrer un contrôle technique
              </button>
            </div>
            {inspectionsCt.length === 0 ? (
              <p className="vide">Aucun contrôle technique pour ce véhicule.</p>
            ) : (
              <TableInspections
                inspections={inspectionsCt}
                onOuvrir={(insp) => navigate(`/vehicules/${id}/ct/${insp.id}`)}
              />
            )}
          </section>
          </>
          )}

          {/* Masque si le module Couts & Carburant n'est pas souscrit. */}
          {modules.module_operations && (
          <section className="carte">
            <div className="entete-section">
              <h3>Coûts &amp; entretien</h3>
              <button
                className="btn-accent"
                onClick={() => navigate(`/vehicules/${id}/operations/nouvelle`)}
              >
                + Ajouter une opération
              </button>
            </div>

            {periodeInversee(dateDebut, dateFin) && (
              <p className="erreur-champ">{MESSAGE_PERIODE_INVERSEE}</p>
            )}
            {!periodeInversee(dateDebut, dateFin) && (
            <div className="stats-couts">
              <div className="stat">
                <span className="stat-label">Coût cumulé</span>
                <span className="stat-valeur">{formaterEuros(couts?.cout_total_ht)}</span>
              </div>
              {/* Carburant et entretien separes : confondus, l'un masque
                  l'autre et on ne sait plus lequel derape. */}
              <div className="stat">
                <span className="stat-label">dont carburant</span>
                <span className="stat-valeur">{formaterEuros(couts?.cout_carburant_ht)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">dont entretien</span>
                <span className="stat-valeur">{formaterEuros(couts?.cout_entretien_ht)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Kilométrage actuel</span>
                <span className="stat-valeur">{formaterKm(couts?.kilometrage_actuel)}</span>
              </div>
              {/* Ces deux-la n'apparaissent QUE si le serveur a pu conclure.
                  Un « 0,00 EUR/km » faute de donnees serait cru. */}
              {couts?.cout_au_km != null && (
                <div className="stat">
                  <span className="stat-label">Coût au kilomètre</span>
                  {/* Deux decimales, comme tout montant en euros (audit B3) : la
                      troisieme -- « 0,354 € » -- donnait une precision que la
                      donnee n'a pas. */}
                  <span className="stat-valeur">{formaterDecimal(couts.cout_au_km, 2)} €/km</span>
                </div>
              )}
              {couts?.consommation_l_100km != null && (
                <div className="stat">
                  <span className="stat-label">Consommation</span>
                  <span className="stat-valeur">{formaterDecimal(couts.consommation_l_100km, 1)} L/100km</span>
                </div>
              )}
              <div className="stat">
                <span className="stat-label">Opérations</span>
                <span className="stat-valeur">{couts?.nombre_operations ?? 0}</span>
              </div>
            </div>
            )}

            <div className="filtres-dates">
              <label>
                Du
                <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
              </label>
              <label>
                Au
                <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
              </label>
              {(dateDebut || dateFin) && (
                <button className="btn" onClick={reinitialiserFiltre}>
                  Réinitialiser
                </button>
              )}
            </div>

            {!periodeInversee(dateDebut, dateFin) && couts?.par_type?.length > 0 && (
              <div className="repartition">
                <h4>Répartition par type</h4>
                <div className="repartition-liste">
                  {couts.par_type.map((t) => (
                    <div key={t.type_operation} className="repartition-item">
                      <span>{labelTypeOperation(t.type_operation)}</span>
                      <span className="repartition-montant">
                        {formaterEuros(t.total_ht)} <span className="sous-info">({t.nombre})</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4>Opérations</h4>
            {periodeInversee(dateDebut, dateFin) ? null : operations.length === 0 ? (
              <p className="vide">Aucune opération sur cette période.</p>
            ) : (
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Réalisé par</th>
                    <th>Km relevé</th>
                    <th>Coût HT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((op) => (
                    <tr key={op.id}>
                      <td>{formaterDate(op.date_operation)}</td>
                      <td>{libelleOperation(op)}</td>
                      <td>{op.realise_par || "—"}</td>
                      <td>{formaterKm(op.kilometrage_releve)}</td>
                      <td>{formaterEuros(op.cout_ht)}</td>
                      <td className="actions-cellule">
                        <button
                          className="lien-action"
                          onClick={() => navigate(`/vehicules/${id}/operations/${op.id}`)}
                        >
                          Modifier
                        </button>
                        <button
                          className="lien-action lien-danger"
                          onClick={() => supprimerOperation(op)}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          )}
        </>
      )}

      {/* Modale de report d'échéance */}
      {modalEcheance && (() => {
        const choisi = typesEcheance.find(
          (t) => t.valeur === nouvelleEcheance.type_recurrence
        );
        return (
          <div className="modal-overlay" onClick={() => setModalEcheance(false)}>
            <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-titre">Ajouter une échéance</h3>
              <p className="modal-sous-titre">{vehicule?.immatriculation}</p>

              <label className="modal-champ">
                Type *
                <select
                  value={nouvelleEcheance.type_recurrence}
                  onChange={(e) => {
                    setErreurEcheance("");
                    setNouvelleEcheance({
                      ...nouvelleEcheance,
                      type_recurrence: e.target.value,
                    });
                  }}
                >
                  {typesEcheance.map((t) => (
                    <option key={t.valeur} value={t.valeur}>
                      {t.libelle}
                    </option>
                  ))}
                </select>
              </label>

              <label className="modal-champ">
                Date de la prochaine échéance *
                <input
                  type="date"
                  value={nouvelleEcheance.date_prochaine_echeance}
                  onChange={(e) =>
                    setNouvelleEcheance({
                      ...nouvelleEcheance,
                      date_prochaine_echeance: e.target.value,
                    })
                  }
                />
              </label>

              {/* Le client saisit la date d'EXPIRATION, pas un point de depart :
                  « mon assurance expire le 12 mars ». Le dire evite la moitie
                  des saisies fausses. */}
              <p className="note-info">
                Indiquez la date à laquelle l'échéance tombe (ex. la fin de votre
                contrat d'assurance).
                {choisi && (
                  <>
                    {" Elle sera ensuite renouvelée automatiquement "}
                    {choisi.jours
                      ? `tous les ${choisi.jours} jours.`
                      : `tous les ${choisi.mois} mois.`}
                  </>
                )}
              </p>

              {erreurEcheance && <p className="modal-erreur">{erreurEcheance}</p>}

              <div className="modal-actions">
                <button className="btn" onClick={() => setModalEcheance(false)}>
                  Annuler
                </button>
                <button
                  className="btn-accent"
                  onClick={ajouterEcheance}
                  disabled={!nouvelleEcheance.date_prochaine_echeance}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* UNE modale pour toutes les confirmations de la page. Le bouton se
          desactive pendant l'appel et l'echec s'affiche ici : voir
          components/ModaleConfirmation.js pour le pourquoi. */}
      {confirmation && (
        <ModaleConfirmation
          {...confirmation}
          onConfirmer={() =>
            confirmation.onConfirmer(
              confirmation.champDate ? dateEffectuee : motifExclusion
            )
          }
          confirmerDesactive={confirmation.champDate && !dateEffectuee}
          onFermer={() => setConfirmation(null)}
          enfants={
            confirmation.champDate ? (
              <label className="modal-champ">
                Date du contrôle
                <input
                  type="date"
                  max={AUJOURDHUI_FICHE}
                  value={dateEffectuee}
                  onChange={(e) => setDateEffectuee(e.target.value)}
                />
              </label>
            ) : confirmation.champMotif ? (
              <label className="modal-champ">
                Motif (ex. Vendu, Accidenté, Hors service)
                <input
                  value={motifExclusion}
                  onChange={(e) => setMotifExclusion(e.target.value)}
                />
              </label>
            ) : null
          }
        />
      )}

      {modalReport && (
        <div className="modal-overlay" onClick={() => setModalReport(null)}>
          <div className="modal-carte" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-titre">Reporter une échéance</h3>
            <p className="modal-sous-titre">
              {labelTypeRecurrence(modalReport.type_recurrence)} — {vehicule?.immatriculation}
            </p>
            <label className="modal-champ">
              Nouvelle date *
              <input
                type="date"
                min={MIN_DATE_REPORT_FICHE}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </label>
            <label className="modal-champ">
              Motif (optionnel)
              <input
                type="text"
                placeholder="Ex. Client indisponible, Panne à traiter avant…"
                value={reportMotif}
                onChange={(e) => setReportMotif(e.target.value)}
              />
            </label>
            <div className="modal-actions">
              <button className="btn" onClick={() => setModalReport(null)}>
                Annuler
              </button>
              <button
                className="btn-accent"
                onClick={soumettreReport}
                disabled={!reportDate || reportEnvoi}
              >
                {reportEnvoi ? "…" : "Confirmer le report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
